import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { Flame, Heart, Loader2, Mail } from "lucide-react";

const PRESETS = [500, 1500, 3000, 5000];

export default function Support() {
  const { t } = useLang();
  const [amount, setAmount] = useState(1500);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { data: recent = [] } = useQuery({ queryKey: ["recentDonations"], queryFn: async () => (await api.get("/donations/recent")).data });

  const effectiveCents = custom ? Math.round(parseFloat(custom) * 100) : amount;

  const donate = async () => {
    if (!effectiveCents || isNaN(effectiveCents) || effectiveCents < 100) { toast.error(t("support_min")); return; }
    setLoading(true);
    try {
      const res = await api.post("/donations/checkout", { amount_cents: effectiveCents, origin_url: window.location.origin, name, message });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); setLoading(false); }
  };

  return (
    <>
      <Seo title={t("support_title")} description={t("support_subtitle")} canonicalPath="/support" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="text-center mb-10">
          <span className="w-16 h-16 rounded-full bg-amber-950/40 border border-amber-800/40 flex items-center justify-center mx-auto mb-6"><Flame className="w-8 h-8 text-amber-400" /></span>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("support_title")}</h1>
          <p className="text-stone-400 max-w-2xl mx-auto mt-4">{t("support_subtitle")}</p>
          <a href="mailto:contact@swiss-pagan.ch" data-testid="support-contact-email" className="inline-flex items-center gap-2 mt-5 text-sm text-amber-400 hover:text-amber-300 transition-colors">
            <Mail className="w-4 h-4" /> contact@swiss-pagan.ch
          </a>
        </div>

        <div className="bg-[#121418] border border-[#272A34] rounded-xl p-6 sm:p-8" data-testid="donation-form">
          <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-3">{t("support_amount")}</label>
          <div className="grid grid-cols-4 gap-3 mb-5">
            {PRESETS.map((c) => (
              <button
                key={c}
                data-testid={`donate-preset-${c}`}
                onClick={() => { setAmount(c); setCustom(""); }}
                className={`py-3 rounded-lg font-serif text-lg border transition-all ${!custom && amount === c ? "bg-amber-600 text-stone-950 border-amber-400 font-bold" : "bg-[#181A20] text-stone-200 border-[#272A34] hover:border-amber-500/40"}`}
              >
                {c / 100}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("support_custom")}</label>
              <input data-testid="donate-custom" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="—" className="w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-stone-500 mb-2">{t("support_name_ph")}</label>
              <input data-testid="donate-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("support_anonymous")} className="w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
            </div>
          </div>

          <div className="mb-6">
            <textarea data-testid="donate-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("support_msg_ph")} rows={2} className="w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 resize-none" />
          </div>

          <button
            data-testid="donate-submit"
            onClick={donate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-6 py-3.5 rounded-md shadow-lg transition-all border border-amber-400/30 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            {t("support_donate")} · CHF {(effectiveCents / 100 || 0).toFixed(0)}
          </button>
        </div>

        <section className="mt-14">
          <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><Heart className="w-5 h-5 text-amber-500" /> {t("support_recent")}</h2>
          {recent.length === 0 ? (
            <p className="text-stone-400" data-testid="donations-empty">{t("support_none")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="donations-wall">
              {recent.map((d, i) => (
                <div key={i} className="bg-[#121418] border border-[#272A34] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-stone-100 truncate">{d.name || t("support_anonymous")}</span>
                    <span className="font-mono text-amber-400 shrink-0 ml-3">CHF {(d.amount_cents / 100).toFixed(0)}</span>
                  </div>
                  {d.message && <p className="text-sm text-stone-400 mt-1.5 line-clamp-2">“{d.message}”</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}
